import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, Truck, Phone, ArrowRight, Shield, Thermometer } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import sodInstallationImage from '@/assets/sod-installation-service.jpg';

const SodDeliveryPage = () => {
  const { language } = useLanguage();

  const sodTypes = [
    {
      name: 'St. Augustine',
      nameEs: 'San Agustín',
      price: '$0.85',
      features: language === 'en' 
        ? ['Heat tolerant', 'Thick, lush growth', 'Good for shade', 'Low maintenance']
        : ['Tolerante al calor', 'Crecimiento espeso y exuberante', 'Bueno para sombra', 'Bajo mantenimiento'],
      description: language === 'en'
        ? 'The most popular choice for Texas lawns. Excellent heat tolerance and beautiful appearance.'
        : 'La opción más popular para céspedes de Texas. Excelente tolerancia al calor y hermosa apariencia.',
      installTime: language === 'en' ? 'Delivery available' : 'Entrega disponible'
    },
    {
      name: 'Bermuda',
      nameEs: 'Bermuda',
      price: '$0.75',
      features: language === 'en'
        ? ['Drought resistant', 'High traffic tolerance', 'Fast establishment', 'Full sun preferred']
        : ['Resistente a sequía', 'Alta tolerancia al tráfico', 'Establecimiento rápido', 'Prefiere sol completo'],
      description: language === 'en'
        ? 'Perfect for high-traffic areas. Extremely durable and fast-growing.'
        : 'Perfecto para áreas de alto tráfico. Extremadamente duradero y de crecimiento rápido.',
      installTime: language === 'en' ? 'Delivery available' : 'Entrega disponible'
    },
    {
      name: 'Zoysia',
      nameEs: 'Zoysia',
      price: '$1.25',
      features: language === 'en'
        ? ['Premium quality', 'Dense growth', 'Soft texture', 'Disease resistant']
        : ['Calidad premium', 'Crecimiento denso', 'Textura suave', 'Resistente a enfermedades'],
      description: language === 'en'
        ? 'Premium grass option with superior quality and feel. Worth the investment.'
        : 'Opción de césped premium con calidad y sensación superiores. Vale la pena la inversión.',
      installTime: language === 'en' ? 'Next day delivery' : 'Entrega al día siguiente'
    }
  ];

  const services = [
    {
      title: language === 'en' ? 'Sod Delivery' : 'Entrega de Césped',
      description: language === 'en'
        ? 'Fresh sod delivered directly to your property with careful handling'
        : 'Césped fresco entregado directamente a tu propiedad con manejo cuidadoso',
      features: language === 'en'
        ? ['Delivery throughout Montgomery County', 'Fresh cut daily', 'Careful handling', 'Quantity verification']
        : ['Entrega el mismo día', 'Corte fresco diario', 'Manejo cuidadoso', 'Verificación de cantidad'],
      price: language === 'en' ? 'Delivery from $75' : 'Entrega desde $75',
      icon: Truck
    },
    {
      title: language === 'en' ? 'Professional Installation' : 'Instalación Profesional',
      description: language === 'en'
        ? 'Expert installation with soil preparation and post-installation care'
        : 'Instalación experta con preparación del suelo y cuidado post-instalación',
      features: language === 'en'
        ? ['Soil preparation', 'Professional laying', 'Edge trimming', 'Initial watering']
        : ['Preparación del suelo', 'Colocación profesional', 'Corte de bordes', 'Riego inicial'],
      price: language === 'en' ? 'Installation quote on request' : 'Cotización de instalación bajo solicitud',
      icon: Shield
    },
    {
      title: language === 'en' ? 'Soil Preparation' : 'Preparación del Suelo',
      description: language === 'en'
        ? 'Complete soil analysis and preparation for optimal sod establishment'
        : 'Análisis y preparación completa del suelo para establecimiento óptimo del césped',
      features: language === 'en'
        ? ['Soil testing', 'pH adjustment', 'Fertilizer application', 'Grading & leveling']
        : ['Prueba de suelo', 'Ajuste de pH', 'Aplicación de fertilizante', 'Nivelación'],
      price: language === 'en' ? 'Preparation quote on request' : 'Cotización de preparación bajo solicitud',
      icon: Thermometer
    }
  ];

  const installationSteps = [
    {
      step: 1,
      title: language === 'en' ? 'Site Preparation' : 'Preparación del Sitio',
      description: language === 'en'
        ? 'We remove existing grass, test soil pH, and level the ground for optimal sod installation.'
        : 'Removemos el césped existente, probamos el pH del suelo y nivelamos el terreno para instalación óptima.',
      timeframe: language === 'en' ? '1-2 hours' : '1-2 horas'
    },
    {
      step: 2,
      title: language === 'en' ? 'Soil Amendment' : 'Mejora del Suelo',
      description: language === 'en'
        ? 'Apply fertilizers, soil conditioners, and amendments based on soil test results.'
        : 'Aplicamos fertilizantes, acondicionadores de suelo y mejoras basadas en resultados de pruebas.',
      timeframe: language === 'en' ? '30 minutes' : '30 minutos'
    },
    {
      step: 3,
      title: language === 'en' ? 'Sod Installation' : 'Instalación del Césped',
      description: language === 'en'
        ? 'Professional laying of fresh sod with tight seams and proper alignment.'
        : 'Colocación profesional de césped fresco con juntas ajustadas y alineación adecuada.',
      timeframe: language === 'en' ? '2-4 hours' : '2-4 horas'
    },
    {
      step: 4,
      title: language === 'en' ? 'Initial Care' : 'Cuidado Inicial',
      description: language === 'en'
        ? 'Thorough watering and care instructions to ensure successful establishment.'
        : 'Riego completo e instrucciones de cuidado para asegurar establecimiento exitoso.',
      timeframe: language === 'en' ? '15 minutes' : '15 minutos'
    }
  ];

  const seoTitle = language === 'en' 
    ? 'Professional Sod Delivery & Installation Texas | Montgomery County Service'
    : 'Entrega e Instalación Profesional de Césped Texas | Servicio el Mismo Día';
  
  const seoDescription = language === 'en'
    ? 'Professional sod delivery and installation in Montgomery County TX. St. Augustine, Bermuda, Zoysia grass. Delivery available throughout Montgomery County, expert installation, soil preparation included.'
    : 'Entrega e instalación profesional de césped en el Condado Montgomery TX. Césped St. Augustine, Bermuda, Zoysia. Entrega el mismo día, instalación experta, preparación del suelo incluida.';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": seoTitle,
    "description": seoDescription,
    "provider": {
      "@type": "LocalBusiness",
      "name": "The Grass Depot LLC"
    },
    "offers": sodTypes.map(sod => ({
      "@type": "Offer",
      "name": `${sod.name} Sod`,
      "price": sod.price.replace('$', ''),
      "priceCurrency": "USD"
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={language === 'en' 
          ? 'sod delivery texas, sod installation montgomery county, st augustine grass delivery, bermuda sod texas, professional sod installation'
          : 'entrega césped texas, instalación césped montgomery county, entrega césped st augustine, césped bermuda texas, instalación profesional césped'
        }
        canonical={language === 'en' ? '/en/services/sod-delivery' : '/es/servicios/entrega-cesped'}
        structuredData={structuredData}
        language={language}
        breadcrumbs={[
          { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` },
          { name: language === 'en' ? 'Services' : 'Servicios', url: language === 'en' ? '/en/services' : '/es/servicios' },
          { name: language === 'en' ? 'Sod Delivery' : 'Entrega de Césped', url: language === 'en' ? '/en/services/sod-delivery' : '/es/servicios/entrega-cesped' },
        ]}
      />
      <Header />
      
      <main>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${sodInstallationImage})` }}
        ></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {language === 'en' ? 'Professional Sod Delivery & Installation' : 'Entrega e Instalación Profesional de Césped'}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8">
              {language === 'en' 
                ? 'Transform your property with premium sod installation services. Delivery available throughout Montgomery County.'
                : 'Transforma tu propiedad con servicios de instalación de césped premium. Entrega el mismo día disponible.'
              }
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <Clock className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Montgomery County Delivery' : 'Entrega Condado Montgomery'}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <Shield className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Professional Installation' : 'Instalación Profesional'}
              </Badge>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/${language}/quote`}>
                <Button variant="hero" size="lg" className="hover-scale">
                  {language === 'en' ? 'Get Free Quote' : 'Obtener Cotización Gratis'}
                </Button>
              </Link>
              <a href="tel:9363200763">
                <Button variant="outline" size="lg" className="border-white/30 text-green-700 hover:bg-white/10 hover-scale">
                  <Phone className="mr-2 h-5 w-5" />
                  {language === 'en' ? 'Call Now' : 'Llamar Ahora'}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sod Types Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Premium Sod Varieties' : 'Variedades de Césped Premium'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'Choose from our selection of premium grass varieties, perfectly suited for Texas climate'
                : 'Elige de nuestra selección de variedades de césped premium, perfectamente adecuadas para el clima de Texas'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {sodTypes.map((sod, index) => (
              <Card key={index} className="hover:shadow-glow transition-all duration-300 border-2 hover:border-primary animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-xl font-bold text-foreground">
                      {language === 'en' ? sod.name : sod.nameEs}
                    </CardTitle>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary">Premium Quality</div>
                      <div className="text-sm text-muted-foreground">
                        {language === 'en' ? 'Fresh Cut' : 'Recién Cortado'}
                      </div>
                    </div>
                  </div>
                  <CardDescription>
                    {sod.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Clock className="h-4 w-4 mr-2 text-primary" />
                      {sod.installTime}
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-foreground mb-2">
                        {language === 'en' ? 'Key Features:' : 'Características Clave:'}
                      </h4>
                      <ul className="space-y-1">
                        {sod.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link to={`/${language}/quote`}>
                      <Button variant="outline" className="w-full hover-scale">
                        {language === 'en' ? 'Select This Sod' : 'Seleccionar Este Césped'}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Complete Sod Services' : 'Servicios Completos de Césped'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'From delivery to installation, we provide comprehensive sod services'
                : 'Desde entrega hasta instalación, proporcionamos servicios integrales de césped'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="text-center hover:shadow-glow transition-all duration-300 animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <CardHeader>
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground">
                      {service.title}
                    </CardTitle>
                    <div className="text-lg font-semibold text-primary">{service.price}</div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-muted-foreground">
                          <CheckCircle className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Professional Installation Process' : 'Proceso de Instalación Profesional'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'Our proven 4-step process ensures perfect sod installation every time'
                : 'Nuestro proceso probado de 4 pasos asegura instalación perfecta de césped cada vez'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {installationSteps.map((step, index) => (
              <div key={index} className="text-center animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse-glow">
                  <span className="text-2xl font-bold text-white">{step.step}</span>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground mb-2">{step.description}</p>
                <Badge variant="outline" className="hover-scale">
                  <Clock className="h-3 w-3 mr-1" />
                  {step.timeframe}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-hero text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 animate-fade-in">
            {language === 'en' 
              ? 'Ready for Your Perfect Lawn?' 
              : '¿Listo para tu Césped Perfecto?'
            }
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto animate-fade-in">
            {language === 'en'
              ? 'Get professional sod installation with delivery available throughout Montgomery County. Free quotes available.'
              : 'Obtén instalación profesional de césped con entrega el mismo día. Cotizaciones gratuitas disponibles.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-scale-in">
            <Link to={`/${language}/quote`}>
              <Button variant="secondary" size="lg" className="hover-scale">
                {language === 'en' ? 'Get Free Quote' : 'Obtener Cotización Gratis'}
              </Button>
            </Link>
            <a href="tel:9363200763">
              <Button variant="outline" size="lg" className="border-white/30 text-green-700 hover:bg-white/10 hover-scale">
                <Phone className="mr-2 h-5 w-5" />
                (936) 320-0763
              </Button>
            </a>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default SodDeliveryPage;